import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Truck, MapPin, CheckCircle2, Package, Clock, Hand, ChevronDown, AlertTriangle, PackageOpen, Timer } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import Timeline from "../components/Timeline";

const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

export default function Ritiri() {
  const { text } = useSettings();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState({});

  const load = async () => {
    const { data } = await api.get("/acquisti", { params: { mine: true } });
    const relevant = data.filter((a) => a.status === "da_ritirare" || a.status === "ritirato");
    setItems(relevant);
    relevant.filter((a) => a.status === "da_ritirare" && !a.seen_at).forEach((a) => api.put(`/acquisti/${a.id}/seen`).catch(() => {}));
  };

  useEffect(() => {
    load().catch(() => {});
    const t = setInterval(() => load().catch(() => {}), 6000);
    return () => clearInterval(t);
  }, []);

  const markPreso = async (id) => {
    try { await api.put(`/acquisti/${id}/preso`); toast.success("Preso in carico"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const markRitirato = async (id) => {
    try { await api.put(`/acquisti/${id}/status`, { status: "ritirato" }); toast.success("Merce ritirata!"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const setPronta = async (id, val) => {
    try { await api.put(`/acquisti/${id}/pronta`, { pronta: val }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const segnalaLento = async (a) => {
    try {
      await api.post("/alerts", { kind: "posteggiante_lento", articolo: a.prodotto,
        posteggio: a.posteggio?.nome, message: "Posteggiante lento: merce non ancora pronta", acquisto_id: a.id });
      toast.warning("Segnalato al buyer: posteggiante lento");
    } catch (err) { toast.error(apiError(err)); }
  };

  const daRitirare = items.filter((a) => a.status === "da_ritirare");
  const ritirati = items.filter((a) => a.status === "ritirato");

  return (
    <div className="animate-fade-up">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("ritiri_title")}</h1>
      <p className="text-slate-400 mb-6">{text("ritiri_subtitle")}</p>

      <h2 className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-3">Da ritirare ({daRitirare.length})</h2>
      <div className="space-y-3 mb-8" data-testid="ritiri-todo">
        {daRitirare.length === 0 && <p className="text-slate-500 text-sm">Nessun ritiro in coda. 👍</p>}
        {daRitirare.map((a) => (
          <div key={a.id} data-testid={`ritiro-${a.id}`} className="bg-slate-900/50 border border-amber-500/30 rounded-md p-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-md bg-amber-500/15 flex items-center justify-center shrink-0">
                <Package className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-lg text-slate-100">{a.prodotto}</div>
                <div className="font-mono-plex text-slate-300">{a.colli} {a.unita}{a.peso_kg ? ` · ${a.peso_kg} kg` : ""}</div>
                <div className="flex items-center gap-1 text-sm text-slate-400 mt-1">
                  <MapPin className="w-4 h-4 text-amber-400" /> {a.posteggio?.nome || "—"}{a.posteggio?.fornitore ? ` (${a.posteggio.fornitore})` : ""}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-1" data-testid={`ritiro-orario-${a.id}`}>
                  <Clock className="w-3.5 h-3.5" /> Acquistato: {fmtTime(a.created_at)}
                  {a.taken_at && <span className="text-emerald-400 ml-2">· Preso in carico ✓</span>}
                </div>
                {a.note && <div className="text-sm text-slate-500 mt-1">{a.note}</div>}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid={`ritiro-prep-${a.id}`}>
              <button data-testid={`ritiro-pronta-${a.id}`} onClick={() => setPronta(a.id, true)}
                className={`flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-2 border transition-colors ${a.pronta ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"}`}>
                <PackageOpen className="w-4 h-4" /> Pronta per ritiro
              </button>
              <button data-testid={`ritiro-daprep-${a.id}`} onClick={() => setPronta(a.id, false)}
                className={`flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-2 border transition-colors ${a.pronta === false ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700"}`}>
                <Timer className="w-4 h-4" /> Ancora da preparare
              </button>
              <button data-testid={`ritiro-lento-${a.id}`} onClick={() => segnalaLento(a)}
                className="flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-2 border bg-red-500/10 text-red-300 border-red-500/40 hover:bg-red-500/20 transition-colors ml-auto">
                <AlertTriangle className="w-4 h-4" /> Posteggiante lento
              </button>
            </div>

            <div className="flex gap-2 mt-3">
              {!a.taken_at && (
                <button data-testid={`ritiro-preso-${a.id}`} onClick={() => markPreso(a.id)}
                  className="flex-1 min-h-[48px] bg-slate-700 text-slate-100 font-bold rounded-md flex items-center justify-center gap-2 hover:bg-slate-600 transition-colors">
                  <Hand className="w-5 h-5" /> Preso in carico
                </button>
              )}
              <button data-testid={`ritiro-done-${a.id}`} onClick={() => markRitirato(a.id)}
                className="flex-1 min-h-[48px] bg-amber-500 text-slate-950 font-bold rounded-md flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors">
                <CheckCircle2 className="w-5 h-5" /> RITIRATO
              </button>
            </div>

            <button data-testid={`ritiro-dettagli-${a.id}`} onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
              className="mt-3 text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open[a.id] ? "rotate-180" : ""}`} /> Stato del processo
            </button>
            {open[a.id] && <div className="mt-3 border-t border-slate-800 pt-3"><Timeline a={a} compact /></div>}
          </div>
        ))}
      </div>

      <h2 className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">Ritirati / in transito ({ritirati.length})</h2>
      <div className="space-y-3" data-testid="ritiri-done">
        {ritirati.map((a) => (
          <div key={a.id} className="bg-slate-900/50 border border-blue-500/20 rounded-md p-4">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-blue-400 shrink-0" />
              <div className="flex-1">
                <span className="font-semibold text-slate-100">{a.prodotto}</span>
                <span className="font-mono-plex text-slate-400 text-sm ml-2">{a.colli} {a.unita}</span>
                <div className="text-sm text-slate-500">{a.posteggio?.nome}</div>
              </div>
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-blue-500/15 text-blue-300 border-blue-500/40">In transito</span>
            </div>
            <button data-testid={`ritiro-dettagli-${a.id}`} onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
              className="mt-2 text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1">
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open[a.id] ? "rotate-180" : ""}`} /> Stato del processo
            </button>
            {open[a.id] && <div className="mt-3 border-t border-slate-800 pt-3"><Timeline a={a} compact /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}
